import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { Box, Card, LinearProgress, Tooltip, Typography } from '@mui/material';

import { getProgressBarColor } from '@/modules/common/application/ui';
import { Chip } from '@/modules/common/components/Chip';

import { useUser } from '../../application/context/user';
import { convertNumberFormat } from '../../application/numberFormat';
import { NumberFormat } from '../../types';
import { getChipColor } from './utils';

interface Props {
  title: string;
  value: number;
  unit: string;
  progress?: number;
  development?: number;
  cardStyles?: React.CSSProperties;
  infoText?: string;
  highlightValue?: boolean;
}

const StatCard = ({
  title,
  value,
  unit,
  progress,
  development,
  cardStyles = {},
  infoText,
  highlightValue = false,
}: Props) => {
  const inProgress = progress !== undefined;
  const inDevelopment = development !== undefined;

  const { user } = useUser();

  const formattedValue = convertNumberFormat(
    value,
    user?.numberFormat || NumberFormat.COMMA_PERIOD
  );

  return (
    <Card
      sx={{
        p: 3,
        border: '1px solid',
        borderColor: 'lightGrey',
        position: 'relative',
        boxShadow: 'none',
        ...cardStyles,
      }}
    >
      <Typography variant="body1" color="text.secondary" component={'span'}>
        {title}
      </Typography>
      {infoText && infoText.length > 0 ? (
        <Box sx={{ mb: 2, position: 'absolute', right: 16, top: 16 }}>
          <Tooltip title={infoText}>
            <InfoOutlinedIcon sx={{ color: 'action.active', fontSize: 20 }} />
          </Tooltip>
        </Box>
      ) : null}
      <Box
        sx={{
          alignItems: 'center',
          display: 'flex',
          flexWrap: 'wrap',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'space-between',
          }}
        >
          <Typography variant="h4" component={'span'} sx={{ mr: 1 }}>
            {formattedValue}
          </Typography>
          <Typography
            variant="h4"
            component={'span'}
            color={highlightValue ? 'text.secondary' : undefined}
            sx={{ mr: 1 }}
          >
            {unit}
          </Typography>
        </Box>

        {(inProgress || inDevelopment) && (
          <Box sx={{ flexGrow: 1 }}>
            {inProgress && (
              <LinearProgress
                data-testid="linear-progress"
                color={getProgressBarColor(progress)}
                value={progress}
                variant="determinate"
              />
            )}
            {inDevelopment && (
              <Chip
                size="small"
                styles={{ maxWidth: 'fit-content' }}
                colorVariant={getChipColor(development)}
              >{`${development} %`}</Chip>
            )}
          </Box>
        )}
      </Box>
    </Card>
  );
};

export default StatCard;
